import { normalizeIsoDate } from "@/lib/mainGanttProjectSpan";

/** Целые пиксели на календарный день — линейная шкала без дробных сдвигов. */
export const PROJECT_GANTT_PX_PER_DAY = 4;

/** Минимальная ширина полосы задачи в px (короткие задачи остаются различимыми). */
export const PROJECT_GANTT_MIN_BAR_PX = 12;

export function isoToLocalDate(iso: string): Date | null {
  const n = normalizeIsoDate(iso);
  if (!n) return null;
  const [y, mo, d] = n.split("-").map(Number);
  return new Date(y, mo - 1, d);
}

export function localTodayDate(): Date {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

export function localTodayIso(): string {
  const d = localTodayDate();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Разница в календарных днях (b − a), без влияния DST. */
export function calendarDaysBetween(a: Date, b: Date): number {
  const t1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const t2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((t2 - t1) / 86400000);
}

export function addCalendarDays(d: Date, delta: number): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + delta);
  return x;
}

export type ProjectGanttTimelineModel = {
  minDay: Date;
  maxDay: Date;
  totalDays: number;
  pxPerDay: number;
  widthPx: number;
  /** Вертикали границ месяцев (px от левого края таймлайна). */
  monthLinePx: number[];
  /** Вертикали понедельников (слабее). */
  weekLinePx: number[];
  /** Подписи месяцев у линий сетки (без «колонок» переменной ширины — ось линейная). */
  monthLabels: { leftPx: number; label: string }[];
  /** Линия «сегодня» или null, если вне диапазона. */
  todayLinePx: number | null;
};

function collectTaskBounds(tasks: { start: string; end: string }[]): { min: Date; max: Date } | null {
  let min: Date | null = null;
  let max: Date | null = null;
  for (const t of tasks) {
    const a = isoToLocalDate(t.start);
    const b = isoToLocalDate(t.end);
    if (!a || !b) continue;
    const lo = a <= b ? a : b;
    const hi = a <= b ? b : a;
    if (!min || lo < min) min = lo;
    if (!max || hi > max) max = hi;
  }
  if (!min || !max) return null;
  return { min, max };
}

/**
 * Диапазон шкалы: задачи + отступ + включение «сегодня» (как раньше по смыслу).
 */
export function buildProjectGanttTimeline(
  tasks: { start: string; end: string }[],
  pxPerDay: number = PROJECT_GANTT_PX_PER_DAY,
  now: Date = localTodayDate(),
): ProjectGanttTimelineModel {
  const bounds = collectTaskBounds(tasks);
  let minDay: Date;
  let maxDay: Date;

  if (!bounds) {
    minDay = addCalendarDays(now, -30);
    maxDay = addCalendarDays(now, 90);
  } else {
    minDay = bounds.min;
    maxDay = bounds.max;
    const spanDays = calendarDaysBetween(minDay, maxDay) + 1;
    const pad = Math.max(14, Math.ceil(spanDays * 0.05));
    minDay = addCalendarDays(minDay, -pad);
    maxDay = addCalendarDays(maxDay, pad);
    if (calendarDaysBetween(minDay, now) < 0) minDay = addCalendarDays(now, -14);
    if (calendarDaysBetween(now, maxDay) < 0) maxDay = addCalendarDays(now, 60);
  }

  const totalDays = calendarDaysBetween(minDay, maxDay) + 1;
  /** Ширина оси строго = число дней × px/день (без «лишних» пикселей справа). */
  const widthPx = Math.max(Math.round(totalDays * pxPerDay), 1);

  const monthLinePx: number[] = [];
  const monthLabels: { leftPx: number; label: string }[] = [];

  let cursor = new Date(minDay.getFullYear(), minDay.getMonth(), 1);
  while (cursor <= maxDay) {
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);

    if (monthEnd >= minDay && monthStart <= maxDay) {
      const off0 = calendarDaysBetween(minDay, monthStart);
      if (off0 >= 0 && off0 < totalDays) {
        monthLinePx.push(Math.round(off0 * pxPerDay));
      }

      const segStart = monthStart < minDay ? minDay : monthStart;
      const leftOff = calendarDaysBetween(minDay, segStart);
      monthLabels.push({
        leftPx: Math.round(leftOff * pxPerDay),
        label: monthStart.toLocaleDateString("ru-RU", { month: "short", year: "2-digit" }),
      });
    }

    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  const monthLinePxSorted = [...new Set(monthLinePx)].sort((a, b) => a - b);

  const weekLinePx: number[] = [];
  for (let i = 0; i < totalDays; i++) {
    const day = addCalendarDays(minDay, i);
    if (day.getDay() === 1) {
      weekLinePx.push(Math.round(i * pxPerDay));
    }
  }

  const todayOff = calendarDaysBetween(minDay, now);
  const todayLinePx =
    todayOff >= 0 && todayOff < totalDays
      ? Math.round(todayOff * pxPerDay + pxPerDay / 2)
      : null;

  return {
    minDay,
    maxDay,
    totalDays,
    pxPerDay,
    widthPx,
    monthLinePx: monthLinePxSorted,
    weekLinePx,
    monthLabels,
    todayLinePx,
  };
}

export function taskBarGeometry(
  task: { start: string; end: string },
  model: ProjectGanttTimelineModel,
  minBarPx: number = PROJECT_GANTT_MIN_BAR_PX,
): { leftPx: number; widthPx: number } | null {
  const a = isoToLocalDate(task.start);
  const b = isoToLocalDate(task.end);
  if (!a || !b) return null;
  const startD = a <= b ? a : b;
  const endD = a <= b ? b : a;

  const visStart = startD < model.minDay ? model.minDay : startD;
  const visEnd = endD > model.maxDay ? model.maxDay : endD;
  if (visEnd < model.minDay || visStart > model.maxDay) return null;

  const off = calendarDaysBetween(model.minDay, visStart);
  const spanDays = Math.max(1, calendarDaysBetween(visStart, visEnd) + 1);
  const leftPx = Math.round(off * model.pxPerDay);
  const maxRight = model.widthPx;
  const rawW = Math.max(minBarPx, Math.round(spanDays * model.pxPerDay));
  const avail = Math.max(0, maxRight - leftPx);
  const widthPx = Math.max(minBarPx, Math.min(rawW, avail));
  return { leftPx, widthPx };
}
